/**
 * Topbar — sticky glassy header: mobile menu button, theme toggle, a
 * notifications bell with a dropdown of derived alerts (WhatsApp errors, etc.),
 * and an account menu (shop, plan, support, disconnect).
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Bell,
  ChevronDown,
  CircleHelp,
  LogOut,
  Menu,
  Moon,
  Store,
  Sun,
  User as UserIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCouriers, useDisconnectWhatsApp, useProfile } from '../hooks/useApi';
import { useLogout, useSession } from '../hooks/usePortal';
import { IS_PORTAL } from '../lib/mode';
import { useTheme } from '../lib/theme';
import { useToast } from '../lib/toast';
import { StoreSwitcher } from './StoreSwitcher';

interface Notice {
  id: string;
  tone: 'danger' | 'warning' | 'info';
  text: string;
}

export function Topbar({ onOpenMobile }: { onOpenMobile: () => void }) {
  const { theme, toggle } = useTheme();
  const { data: profile } = useProfile();
  const navigate = useNavigate();

  // Derive simple in-app notifications from the current state.
  const notices: Notice[] = [];
  if (profile?.whatsappStatus === 'error') {
    notices.push({
      id: 'wa-err',
      tone: 'danger',
      text: 'WhatsApp disconnected — reconnect to keep messaging.',
    });
  }
  if (profile && profile.whatsappStatus === 'not_connected') {
    notices.push({
      id: 'wa-none',
      tone: 'warning',
      text: 'Connect WhatsApp to start sending order updates.',
    });
  }

  return (
    <header className="glass sticky top-0 z-30 flex h-16 items-center justify-between border-b border-line bg-surface/80 px-4 sm:px-6">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onOpenMobile}
          className="flex h-9 w-9 items-center justify-center rounded-xl text-ink-muted hover:bg-surface-subtle lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        {/* Multi-store switcher (portal only). */}
        {IS_PORTAL ? <StoreSwitcher /> : null}
        {/* Always-visible connection health — the whole flow rides on these. */}
        <ConnectionStatus onClick={() => navigate('/settings')} />
      </div>

      <div className="flex items-center gap-1.5">
        <NotificationsBell notices={notices} onGoSettings={() => navigate('/settings')} />

        <button
          type="button"
          onClick={toggle}
          aria-label="Toggle theme"
          className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-subtle hover:text-ink"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={theme}
              initial={{ y: 12, opacity: 0, rotate: -30 }}
              animate={{ y: 0, opacity: 1, rotate: 0 }}
              exit={{ y: -12, opacity: 0, rotate: 30 }}
              transition={{ duration: 0.2 }}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </motion.span>
          </AnimatePresence>
        </button>

        <AccountMenu />
      </div>
    </header>
  );
}

/**
 * Compact, always-visible health pills for WhatsApp + Courier — the two
 * connections the whole product depends on. Green = connected, amber/red = needs
 * action. Clicking jumps to Settings to fix it. Hidden on small screens.
 */
function ConnectionStatus({ onClick }: { onClick: () => void }) {
  const { data: profile } = useProfile();
  const { data: couriers } = useCouriers();

  const waOk = profile?.whatsappStatus === 'connected';
  const courierOk = (couriers?.connected?.length ?? 0) > 0;

  const Pill = ({ ok, label }: { ok: boolean; label: string }) => (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
        ok
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
          : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-emerald-500' : 'bg-rose-500'}`} />
      {label}
    </span>
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-1 hidden items-center gap-1.5 sm:flex"
      title="Connection health — click to manage in Settings"
    >
      <Pill ok={waOk} label="WhatsApp" />
      <Pill ok={courierOk} label="Courier" />
    </button>
  );
}

function NotificationsBell({
  notices,
  onGoSettings,
}: {
  notices: Notice[];
  onGoSettings: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(() => setOpen(false));
  const toneDot: Record<Notice['tone'], string> = {
    danger: 'bg-rose-500',
    warning: 'bg-amber-500',
    info: 'bg-brand-500',
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className="relative flex h-9 w-9 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-subtle hover:text-ink"
      >
        <Bell className="h-4 w-4" />
        {notices.length > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">
            {notices.length}
          </span>
        ) : null}
      </button>
      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 overflow-hidden rounded-2xl border border-line bg-surface shadow-pop"
          >
            <div className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">
              Notifications
            </div>
            {notices.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-ink-subtle">
                You're all caught up 🎉
              </div>
            ) : (
              <ul className="max-h-80 overflow-y-auto">
                {notices.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        onGoSettings();
                      }}
                      className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-surface-subtle"
                    >
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${toneDot[n.tone]}`} />
                      <span className="text-sm text-ink">{n.text}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function AccountMenu() {
  const { data: profile } = useProfile();
  const { data: user } = useSession();
  const disconnect = useDisconnectWhatsApp();
  const logout = useLogout();
  const toast = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(() => setOpen(false));

  // In portal mode the identity is the user (email); embedded it's the shop —
  // prefer the real store name over the raw myshopify handle.
  const title = IS_PORTAL
    ? user?.email || 'Account'
    : profile?.storeName || profile?.shopDomain || 'your-store';
  const initial = title.charAt(0).toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-xl border border-line py-1 pl-1 pr-2 transition-colors hover:bg-surface-subtle"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-xs font-bold text-white">
          {initial}
        </span>
        <ChevronDown className="h-4 w-4 text-ink-subtle" />
      </button>
      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-64 overflow-hidden rounded-2xl border border-line bg-surface shadow-pop"
          >
            <div className="flex items-center gap-3 border-b border-line px-4 py-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white">
                {initial}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{title}</p>
                <p className="text-xs text-ink-subtle">
                  {(profile?.plan ?? 'free').toUpperCase()} plan
                </p>
              </div>
            </div>
            <div className="py-1">
              {IS_PORTAL ? (
                <MenuItem
                  icon={UserIcon}
                  label="Account & stores"
                  onClick={() => {
                    setOpen(false);
                    navigate('/profile');
                  }}
                />
              ) : null}
              <MenuItem
                icon={Store}
                label="Settings"
                onClick={() => {
                  setOpen(false);
                  navigate('/settings');
                }}
              />
              <MenuItem
                icon={CircleHelp}
                label="Help & support"
                onClick={() => {
                  setOpen(false);
                  window.open('mailto:support@codshield.app');
                }}
              />
              {profile?.whatsappStatus === 'connected' ? (
                <MenuItem
                  icon={AlertTriangle}
                  label="Disconnect WhatsApp"
                  danger
                  onClick={() => {
                    setOpen(false);
                    disconnect.mutate(undefined, {
                      onSuccess: () => toast.info('WhatsApp disconnected'),
                    });
                  }}
                />
              ) : null}
              {IS_PORTAL ? (
                <MenuItem
                  icon={LogOut}
                  label="Sign out"
                  danger
                  onClick={() => {
                    setOpen(false);
                    logout.mutate(undefined, {
                      onSuccess: () => navigate('/login', { replace: true }),
                    });
                  }}
                />
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: typeof Store;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-surface-subtle ${
        danger ? 'text-rose-600' : 'text-ink'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

/** Close a dropdown when clicking outside it. */
function useOutsideClose(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return ref;
}
